import { Box, BoxProps } from '@mantine/core';
import { ElementType, forwardRef, ReactNode, Ref } from 'react';

type BlurSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

type Props<C extends ElementType = typeof Box> = {
  size?: BlurSize;
  component?: C;
  children?: ReactNode;
} & BoxProps;

// CSS blur amounts are half of the Figma amounts
const BLUR_AMOUNTS: Record<BlurSize, number> = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 20,
  xxl: 24,
};

function backdropBlurStyles({ size = 'md' }: { size?: BlurSize } = {}) {
  return {
    backdropFilter: `blur(${BLUR_AMOUNTS[size]}px)`,
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
  };
}

function BackdropBlurComponent<C extends ElementType = typeof Box>(
  { size = 'md', component, style, ...props }: Props<C>,
  ref: Ref<HTMLElement>,
) {
  const Component = (component || Box) as ElementType;

  return (
    <Component
      ref={ref}
      {...props}
      style={{
        ...backdropBlurStyles({ size }),
        ...style,
      }}
    />
  );
}

const BackdropBlur = forwardRef(BackdropBlurComponent) as <C extends ElementType = typeof Box>(
  props: Props<C> & { ref?: Ref<HTMLElement> },
) => JSX.Element;

export default BackdropBlur;
